import { LayoutItem } from 'react-grid-layout';
import {
  CommandDashboardTileDefinition,
  CommandDashboardTileKey,
  tileSizeForColumns,
} from './tile-config';

export const GRID_COLS = 12;

/**
 * Tiles in a row split the width evenly, so the maximum must divide GRID_COLS
 * to avoid leftover columns.
 */
export const MAX_TILES_PER_ROW = 3;

/**
 * One grid row is one pixel. Combined with a zero vertical margin this makes an
 * item's pixel height exactly its `h`, so a tile can be sized to fit its
 * measured content precisely rather than rounded up to the next whole row.
 */
export const ROW_HEIGHT = 1;

/** Gutter between tiles sitting side by side in a row - matches ROW_GAP for a uniform 24px
 * standard spacing between tiles in both directions. */
export const TILE_GAP = 24;

/**
 * Gutter between rows.
 *
 * The grid has no vertical margin, so this is carried inside each row's height
 * and the tile reserves it as padding rather than filling it.
 */
export const ROW_GAP = 24;

/**
 * Fraction at each end of a row that creates a new row instead of joining the
 * one under the cursor. Without it there would be no way to deliberately start
 * a new full-width row between two existing ones.
 */
const NEW_ROW_EDGE_FRACTION = 0.25;

export type TileRows = CommandDashboardTileKey[][];
export type TileDefinitionRows = CommandDashboardTileDefinition[][];

export interface RowBand {
  top: number;
  height: number;
}

export interface TileTarget {
  rowIndex: number;
  position: number;
  asNewRow: boolean;
}

/** Columns each tile occupies when `count` tiles share a row. */
export function widthForRowLength(count: number): number {
  return GRID_COLS / count;
}

/**
 * Plain items consume one of the three available row units. Wide dashboard
 * tiles consume two, allowing them to pair only with one standard tile.
 */
const DEFAULT_ROW_UNITS_FOR = () => 1;

function usedRowUnits<T>(items: T[], rowUnitsFor: (item: T) => number): number {
  return items.reduce((total, item) => total + rowUnitsFor(item), 0);
}

/**
 * A row is exactly as tall as the tallest size its tiles are drawn at, never
 * what their content happens to measure. The sizes are the design's, so a
 * half-width tile is 480px whatever it holds, and a body that wants more room
 * scrolls inside the tile rather than stretching the row past its size.
 */
function rowHeightFor(
  row: CommandDashboardTileDefinition[],
  widths: number[]
): number {
  const tallest = row.reduce(
    (max, tile, index) =>
      Math.max(max, tileSizeForColumns(tile, widths[index]).height),
    0
  );
  return tallest + ROW_GAP;
}

export function columnWidthsForRow(
  row: CommandDashboardTileDefinition[]
): number[] {
  if (row.length === 2) {
    const [first, second] = row;
    if (first.sizeProfile === 'wide' && second.sizeProfile !== 'wide') {
      return [8, 4];
    }
    if (second.sizeProfile === 'wide' && first.sizeProfile !== 'wide') {
      return [4, 8];
    }
  }
  const w = widthForRowLength(row.length);
  return row.map(() => w);
}

/**
 * Builds a layout where each row's tiles share a `y` and an `h`, so their
 * borders align, and sit side by side at the widths `columnWidthsForRow` gives
 * them.
 */
export function buildTileLayout(tileRows: TileDefinitionRows): LayoutItem[] {
  const items: LayoutItem[] = [];
  let y = 0;

  tileRows.forEach((row) => {
    if (row.length === 0) {
      return;
    }
    const widths = columnWidthsForRow(row);
    const h = rowHeightFor(row, widths);

    let x = 0;
    row.forEach((tile, index) => {
      const w = widths[index];
      items.push({ i: tile.key, x, y, w, h });
      x += w;
    });
    y += h;
  });

  return items;
}

/** Vertical band each row occupies, for hit testing a drop position. */
export function rowGeometry(tileRows: TileDefinitionRows): RowBand[] {
  const bands: RowBand[] = [];
  let top = 0;

  tileRows.forEach((row) => {
    if (row.length === 0) {
      return;
    }
    const widths = columnWidthsForRow(row);
    const height = rowHeightFor(row, widths);
    bands.push({ top, height });
    top += height;
  });

  return bands;
}

/**
 * Horizontal cursor position in column units.
 *
 * A column plus its gutter is a constant share of the container, so the offset
 * from the grid's left edge converts straight into a fractional column.
 */
export function columnAt(offsetX: number, containerWidth: number): number {
  return offsetX / ((containerWidth + TILE_GAP) / GRID_COLS);
}

/** Index at which a tile at `cursorCol` belongs within a row of `count` tiles. */
function positionInRow(count: number, cursorCol: number): number {
  const slotWidth = widthForRowLength(count);
  let position = 0;
  for (let index = 0; index < count; index += 1) {
    if ((index + 0.5) * slotWidth < cursorCol) {
      position += 1;
    }
  }
  return position;
}

/**
 * Where a tile held at `cursorCol` columns across and `cursorY` pixels down
 * would land.
 *
 * Vertical positions are pixels because the grid uses a row height of one and
 * no vertical margin, so a layout `y` is already a pixel offset.
 *
 * `draggingItem` and `rowUnitsFor` account for wide tiles consuming two of a
 * row's three units.
 */
export function resolveTarget<T>(
  rows: T[][],
  bands: RowBand[],
  cursorCol: number,
  cursorY: number,
  draggingItem?: T,
  rowUnitsFor: (item: T) => number = DEFAULT_ROW_UNITS_FOR
): TileTarget {
  for (let rowIndex = 0; rowIndex < bands.length; rowIndex += 1) {
    const band = bands[rowIndex];
    if (cursorY >= band.top + band.height) {
      continue;
    }

    const local = (cursorY - band.top) / band.height;

    if (local < NEW_ROW_EDGE_FRACTION) {
      return { rowIndex, position: 0, asNewRow: true };
    }
    if (local > 1 - NEW_ROW_EDGE_FRACTION) {
      return { rowIndex: rowIndex + 1, position: 0, asNewRow: true };
    }

    const row = rows[rowIndex] ?? [];
    const rowLength = row.length;
    const candidates =
      draggingItem === undefined ? row : [...row, draggingItem];
    if (
      usedRowUnits(row, rowUnitsFor) >= MAX_TILES_PER_ROW ||
      usedRowUnits(candidates, rowUnitsFor) > MAX_TILES_PER_ROW
    ) {
      return { rowIndex: rowIndex + 1, position: 0, asNewRow: true };
    }

    return {
      rowIndex,
      position: positionInRow(rowLength, cursorCol),
      asNewRow: false,
    };
  }

  return { rowIndex: bands.length, position: 0, asNewRow: true };
}

/**
 * Rows with `tile` added at `target`, leaving every other row untouched.
 *
 * Shared by the store, which commits a drop, and the grid, which renders the
 * same arrangement as a preview while the drag is still in flight, so what the
 * user sees is what they get.
 *
 * `rowUnitsFor` prevents a row from exceeding its three-unit capacity.
 */
export function insertTile<T>(
  rows: T[][],
  tile: T,
  target: TileTarget,
  rowUnitsFor: (item: T) => number = DEFAULT_ROW_UNITS_FOR
): T[][] {
  const next = rows.map((row) => [...row]);

  if (target.asNewRow) {
    next.splice(Math.max(0, Math.min(target.rowIndex, next.length)), 0, [tile]);
    return next;
  }

  const row = next[target.rowIndex];
  if (!row || usedRowUnits([...row, tile], rowUnitsFor) > MAX_TILES_PER_ROW) {
    next.push([tile]);
    return next;
  }

  row.splice(Math.max(0, Math.min(target.position, row.length)), 0, tile);
  return next;
}

/**
 * Rows with `tile` taken out of wherever it sits and put back at `target`.
 *
 * Shared by the store, which commits a move, and the grid, which renders the
 * same arrangement as a preview while the drag is still in flight.
 */
export function moveTileTo<T>(
  rows: T[][],
  tile: T,
  target: TileTarget,
  rowUnitsFor: (item: T) => number = DEFAULT_ROW_UNITS_FOR
): T[][] {
  const sourceRowIndex = rows.findIndex((row) => row.includes(tile));
  if (sourceRowIndex === -1) {
    return rows;
  }

  const remaining = rows
    .map((row) => row.filter((candidate) => candidate !== tile))
    .filter((row) => row.length > 0);

  const collapsed = rows[sourceRowIndex].length === 1;

  // The tile had this row to itself, so the row it is being dropped on is the
  // one it is being taken out of and nothing about the arrangement changes.
  // Without this the row would be gone by the time the tile was put back and
  // the index would land on whichever row had closed the gap.
  if (collapsed && !target.asNewRow && target.rowIndex === sourceRowIndex) {
    return rows;
  }

  // Taking the tile out empties a row it had to itself, and the rows below
  // close the gap, so a target beneath it has to move up to match.
  const rowIndex =
    collapsed && target.rowIndex > sourceRowIndex
      ? target.rowIndex - 1
      : target.rowIndex;

  return insertTile(remaining, tile, { ...target, rowIndex }, rowUnitsFor);
}

export function isSameTarget(
  a: TileTarget | null,
  b: TileTarget | null
): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  return (
    a.rowIndex === b.rowIndex &&
    a.position === b.position &&
    a.asNewRow === b.asNewRow
  );
}
